import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {extractErrorMessage} from "../../../common/common";
import {LoaderComponent} from "../../common/loader/loader.component";
import {RegisteredReportViewModel} from 'src/app/model/registeredReports.model';
import {RidViewModel} from 'src/app/model/rids.model';

@Component({
  selector: 'app-send-backfill-report',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './send-backfill-report.component.html',
  styleUrl: './send-backfill-report.component.scss'
})
export class SendBackfillReportComponent implements OnInit {
  protected isLoading: boolean = false;

  // form
  private isoTimeRegex: RegExp = /^(?![+-]?\d{4,5}-?(?:\d{2}|W\d{2})T)(?:|(\d{4}|[+-]\d{5})-?(?:|(0\d|1[0-2])(?:|-?([0-2]\d|3[0-1]))|([0-2]\d{2}|3[0-5]\d|36[0-6])|W([0-4]\d|5[0-3])(?:|-?([1-7])))(?:(?!\d)|T(?=\d)))(?:|([01]\d|2[0-4])(?:|:?([0-5]\d)(?:|:?([0-5]\d)(?:|\.(\d{3})))(?:|[zZ]|([+-](?:[01]\d|2[0-4]))(?:|:?([0-5]\d)))))$/;
  requestForm = new FormGroup({
    endTime: new FormControl<string>('', [Validators.required, Validators.pattern(this.isoTimeRegex)]),
    startTime: new FormControl<string>('', [Validators.required, Validators.pattern(this.isoTimeRegex)]),
  });

  public rids: RidViewModel[] | undefined = [];

  public report: RegisteredReportViewModel[] = [];

  protected result: any = {
    num_rids: 0,
    succeeded: '',
    updated: 0,
    missing: '',
    available: '',
    expected: '',
    num_interval_dttms: 0,
    ven: {
      id: 0
    },
    report_id: 0,
    request_dttm: 0,
    start_dttm: 0,
    end_dttm: 0,
  };

  protected status = 'Waiting.';
  protected error: string | null = null;
  protected confirm_needed = false;
  protected basic_source_error = false;

  protected reportSelected: RegisteredReportViewModel | undefined;

  protected rid_selected: RidViewModel[] = [];
  protected start_dttm: any;
  protected end_dttm: any;
  protected check: any = false;
  protected formsSubscribe: any;
  protected unsubscribe =  new Subject<void>();

  constructor(private sendReportService: ApiService){}

  ngOnInit(): void {
    this.isLoading = true;
    const reportsQueryParams: CustomHttpParams = {
      'depths': JSON.stringify({
        "metadata_report": 1,
        "rids": 1,
        "ven": 1
      }),
    };
    this.sendReportService.getRegisteredReports(reportsQueryParams).subscribe((response: RegisteredReportViewModel[]) => {
      this.report = response;
      this.isLoading = false;
    });

    this.formsSubscribe = this.requestForm.controls.endTime.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.end_dttm = val ?? '';
      }
    );

    this.formsSubscribe = this.requestForm.controls.startTime.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.start_dttm = val ?? '';
      }
    );
  }

  setRegisteredReports(event: any) {
    this.reportSelected = this.report.find((response: any) => response.id === Number(event.value)) ?? this.reportSelected;
    this.rids = this.reportSelected?.rids;
    this.basic_source_error = this.reportSelected?.metadata_report?.data_source === 'basic';
    this.setStreamed(this.reportSelected?.metadata_report?.data_source !== 'enbala');
  }

  setStreamed(event: any) {
    if(event.value !== undefined) {
      this.check = event.checked;
    } else {
      this.check = event;
    }
  }

  setRid(event: any) {
    this.rid_selected = Array.from(event.options)
      .filter((ev: any) => ev.selected)
      .map((evtSelected: any) => evtSelected.value);
  }

  validateForm(): boolean {
    if (!this.reportSelected?.id) {
      this.error = "Please select a registered report.";
      return false;
    }

    if (!this.start_dttm) {
      this.error = "Start time is required.";
      return false;
    }

    if (!this.end_dttm) {
      this.error = "End time is required.";
      return false;
    }

    if (this.basic_source_error) {
      this.error = "Cannot backfill reports with BASIC data source.";
      return false;
    }

    if (this.requestForm.invalid) {
      this.error = this.requestForm.get('startTime')?.invalid || this.requestForm.get('endTime')?.invalid ?
        "Start time and end time must be valid ISO timestamps." : "A required field is missing.";
      return false;
    }

    const start = new Date(this.start_dttm);
    const end = new Date(this.end_dttm);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      this.error = "Invalid date format.";
      return false;
    }

    if (start >= end) {
      this.error = 'Start time must be before end time.';
      return false;
    }

    this.error = null;
    return true;
  }

  performReport() {
    if (!this.validateForm()) {
      return;
    }

    if (this.confirm_needed) {
      this.confirm();
      return;
    }

    // Show confirmation step if needed
    const oldestAvailableDate = new Date();
    oldestAvailableDate.setUTCDate(oldestAvailableDate.getUTCDate() - 2);

    // Proceed directly
    this.confirm();
  }

  confirm() {
    this.confirm_needed = false;
    this.status = 'Backfill report started.';

    const obj: any = {
      report: this.reportSelected,
      rids: this.rid_selected,
      start_dttm: this.start_dttm,
      end_dttm: this.end_dttm,
      report_id: this.reportSelected?.id,
      streamed: this.check,
    };

    this.isLoading = true;
    this.sendReportService.backFillReport(obj).subscribe({
      next: (response: any) => {
        this.result = response;
        this.status = 'Finished.';
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error(err);
        this.error = extractErrorMessage(err);
        this.status = 'Failed.';
        this.isLoading = false;
      }
    });
  }

  cancel() {
    this.confirm_needed = false;
  }
}
